import { Box, Button, Container, Grid, Card, TextField, MenuItem } from '@mui/material';

import { Formik, Form } from 'formik';
import { FC, useEffect, useState } from 'react';
import { Loader, Page } from '../../../components';
import * as Yup from 'yup';
import { useToastContext } from '../../../hooks';
import { FileUploader } from '../../../components/file/file-uploader';
import { createPeople } from '../../../fetch/imports';
import { Save } from '@mui/icons-material';
import { ConfirmationDialogue } from '../../../components/modal/ConfirmationDialogue';
import { makeStyles } from '@mui/styles';
import { IDropdownResponse, Maybe } from '../../../models';
import { theme } from '../../../styles';
import { getTeams } from '../../../fetch/teams';

const Schema = Yup.object().shape({
  importName: Yup.string().required('Required'),
  fileName: Yup.string().required('Required'),
  teamId: Yup.number().required('Required'),
});

export const ImportPeople: FC = () => {
  // Non-state
  const { showToast } = useToastContext();
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [errorsList, setErrorsList] = useState<Maybe<string[]>>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successfulRecords, setSuccessfulRecords] = useState(0);
  const [isLoadingTeams, setIsLoadingTeams] = useState<boolean>(false);
  const [teams, setTeams] = useState<Maybe<IDropdownResponse[]>>(null);

  const fetchTeams = async () => {
    setIsLoadingTeams(true);
    try {
      const res = await getTeams();
      setTeams(res);
    } catch (err) {
      const message = err instanceof Error && err.message;
      showToast('error', message || 'Failed to fetch Teams.');
    } finally {
      setIsLoadingTeams(false);
    }
    // Ignore showToast dep
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  useEffect(() => {
    fetchTeams();
    // Fetch only on component mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const classes = useStyles();
  return (
    <Page title="Import People">
      <Container maxWidth={false} disableGutters>
        <>
          <Formik
            enableReinitialize={true}
            initialValues={{
              importName: '',
              fileName: '',
              teamId: '',
            }}
            validationSchema={Schema}
            onSubmit={async (values, actions) => {
              try {
                const res = await createPeople(values.importName, uploadedFile, values.teamId);
                if (res && res.Detail) {
                  showToast('error', `Error, ${res.Detail}`);
                  setErrorsList(res.Detail.split('\r\n'));
                  return;
                } else if (res?.errors?.length > 0 || res?.validationErrors?.length > 0) {
                  setErrorsList(
                    res.validationErrors.concat(res.errors).map((error: string) => `${error}\r\n`)
                  );
                  return;
                }
                setSuccessfulRecords(res?.successCount ?? 0);
                setShowSuccess(true);
                // reset state
                actions.resetForm();
              } catch (error: any) {
                console.log(error);
                if (
                  error &&
                  error.errors &&
                  Object.values(error.Errors)[0] &&
                  Object.values(Object.values(error.Errors)[0] as any)[0]
                ) {
                  setErrorsList(error?.Errors.split('\r\n'));
                } else {
                  showToast('error', 'Error importing document, please try again.');
                }
              }
            }}
          >
            {({
              resetForm,
              isSubmitting,
              values,
              initialValues,
              setFieldValue,
              errors,
              touched,
              handleSubmit,
              dirty,
              isValid,
              handleBlur,
            }) => {
              return (
                <Card className={classes.uploader} elevation={2}>
                  <Form onSubmit={handleSubmit} autoComplete="none">
                    <Grid container>
                      <Grid container item spacing={1} xs={12} sm={6}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="Import Name"
                            placeholder="Enter an import name..."
                            name="importName"
                            value={values.importName}
                            onBlur={handleBlur}
                            size="small"
                            required={true}
                            onChange={e => setFieldValue('importName', e.target.value)}
                            error={errors && errors.importName ? true : false}
                            helperText={errors && errors.importName}
                            inputProps={{ maxLength: 300 }}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            select
                            disabled={isLoadingTeams}
                            variant="outlined"
                            autoComplete="nope"
                            label="Team"
                            placeholder="Select a team"
                            name="team"
                            value={values.teamId}
                            onBlur={handleBlur}
                            size="small"
                            required={true}
                            onChange={e => setFieldValue('teamId', parseInt(e.target.value))}
                            error={errors && errors.teamId ? true : false}
                            helperText={errors && errors.teamId}
                          >
                            {teams &&
                              teams.map((team, index) => {
                                return (
                                  <MenuItem key={`${index + 1}`} value={team.value}>
                                    {team.description}
                                  </MenuItem>
                                );
                              })}
                          </TextField>
                        </Grid>
                        <Grid item xs={12}>
                          <FileUploader
                            handleFile={async val => {
                              setUploadedFile(val);
                              setFieldValue('fileName', val?.name ?? '');
                            }}
                            handleRemove={() => {
                              setUploadedFile(null);
                              setFieldValue('fileName', '');
                            }}
                            removeText={'Remove'}
                            templateFile={values.fileName}
                            isRequired={true}
                            dropzoneText="File to Import (.xlsx)"
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Box marginTop="2rem">
                      <Button
                        disabled={!dirty || isSubmitting || !isValid}
                        type="submit"
                        startIcon={<Save />}
                        color="secondary"
                        variant="contained"
                      >
                        Upload
                      </Button>
                    </Box>
                    {errorsList && (
                      <ConfirmationDialogue
                        open
                        id="errors-tier"
                        title="Upload Errors"
                        text={errorsList?.join('\r\n')}
                        hideCancel
                        onClose={() => {
                          setErrorsList(null);
                          setUploadedFile(null);
                          setFieldValue('fileName', '');
                        }}
                        onConfirm={() => {
                          setErrorsList(null);
                          setUploadedFile(null);
                          setFieldValue('fileName', '');
                        }}
                      />
                    )}
                    {showSuccess && (
                      <ConfirmationDialogue
                        open
                        id="success-tier"
                        title="Upload Success"
                        text={`Successfully updated ${successfulRecords} records`}
                        hideCancel
                        onClose={() => {
                          setShowSuccess(false);
                          setSuccessfulRecords(0);
                        }}
                        onConfirm={() => {
                          setShowSuccess(false);
                          setSuccessfulRecords(0);
                        }}
                      />
                    )}
                    {isSubmitting && (
                      <Loader position="centered" type="fullscreen">
                        Importing People...
                      </Loader>
                    )}
                  </Form>
                </Card>
              );
            }}
          </Formik>
        </>
      </Container>
    </Page>
  );
};

const useStyles = makeStyles(() => ({
  uploader: {
    padding: theme.spacing(2),
  },
}));
